
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PricingCard from "@/components/pricing/PricingCard";
import BillingToggle from "@/components/pricing/BillingToggle";
import PricingTestimonials from "@/components/pricing/PricingTestimonials";
import { getSubscriptionTiers, getUserSubscription } from "@/services/subscriptionService";
import { SubscriptionTier } from "@/components/admin/types";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";

interface EnhancedTier extends SubscriptionTier {
  monthlyPrice: number;
  yearlyPrice: number;
  isPopular?: boolean;
  isCurrent?: boolean;
}

const PricingPage = () => {
  const [tiers, setTiers] = useState<EnhancedTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [currentUserTier, setCurrentUserTier] = useState<string | null>(null);
  const [currentBillingPeriod, setCurrentBillingPeriod] = useState<'monthly' | 'yearly' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscribed, openCustomerPortal, checkSubscription } = useSubscription();
  
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load current user subscription with billing period
        if (user) {
          const subscription = await getUserSubscription(user.id);
          setCurrentUserTier(subscription?.tier?.name.toLowerCase() || 'free');
          setCurrentBillingPeriod(subscription?.billingPeriod || null);
          console.log('Loaded user subscription:', { 
            tier: subscription?.tier?.name.toLowerCase(), 
            billingPeriod: subscription?.billingPeriod 
          });
        }

        // Load subscription tiers from database
        const subscriptionTiers = await getSubscriptionTiers();
        console.log('Loaded subscription tiers from database:', subscriptionTiers);
        
        if (subscriptionTiers.length > 0) {
          // Transform database tiers to enhanced tiers with UPDATED PRICING
          const enhancedTiers = subscriptionTiers
            .map(tier => ({
              ...tier,
              monthlyPrice: tier.name.toLowerCase() === 'free' ? 0 :
                         tier.name.toLowerCase() === 'plus' ? 10 : 
                         tier.name.toLowerCase() === 'pro' ? 20 : tier.price,
              yearlyPrice: tier.name.toLowerCase() === 'free' ? 0 :
                         tier.name.toLowerCase() === 'plus' ? 100 : 
                         tier.name.toLowerCase() === 'pro' ? 200 : tier.price * 10,
              isPopular: tier.name.toLowerCase() === 'plus',
              isCurrent: false // We'll handle this in PricingCard now
            }))
            .sort((a, b) => a.price - b.price);
          
          console.log('Enhanced tiers with database features:', enhancedTiers);
          setTiers(enhancedTiers);
        } else {
          console.error('No subscription tiers found in database');
          toast.error('Failed to load subscription plans from database');
        }
      } catch (error) {
        console.error('Error loading pricing data:', error);
        toast.error('Failed to load subscription plans');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [user]);
  
  const handleSelectTier = async (
    tierId: string, 
    period: 'monthly' | 'yearly', 
    actionType?: 'upgrade' | 'downgrade' | 'cancel' | 'switch'
  ) => {
    if (!user) {
      navigate('/sign-in', { state: { returnUrl: '/pricing' } });
      return;
    }
    
    console.log('handleSelectTier called:', { tierId, period, actionType, currentUserTier, currentBillingPeriod });
    
    // Handle cancellation by redirecting to customer portal
    if (tierId === 'free' && actionType === 'cancel') {
      try {
        await openCustomerPortal();
      } catch (error) {
        console.error('Error opening customer portal for cancellation:', error);
        toast.error('Failed to open subscription management. Please try again.');
      }
      return;
    }

    if (tierId === 'free' && !actionType) {
      toast.info('You are already on the free plan!');
      return;
    }

    if (isProcessing) return;
    setIsProcessing(true);

    try {
      console.log(`Processing subscription action: ${actionType || 'new'} to ${tierId} ${period}`);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          tierId, // This is now the tier name (lowercase)
          billingPeriod: period,
          isUpgrade: actionType === 'upgrade' || actionType === 'downgrade' || actionType === 'switch'
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        console.log('Opening Stripe checkout:', data.url);
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
      } else if (data?.success) {
        // Direct subscription update was successful
        toast.success(data.message || 'Subscription updated successfully!');
        // Refresh subscription status
        await checkSubscription();
      } else {
        throw new Error('No checkout URL or success confirmation returned');
      }
    } catch (error) {
      console.error('Subscription action error:', error);
      toast.error('Failed to process subscription change. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Start free and scale with our AI-powered productivity tools
            </p>
          </div>
          
          <BillingToggle 
            billingPeriod={billingPeriod}
            onToggle={setBillingPeriod}
          />
          
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : tiers.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">No Plans Available</h3>
                <p className="text-muted-foreground">Unable to load subscription plans. Please try again later.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto pt-6">
              {tiers.map((tier) => (
                <PricingCard
                  key={tier.id}
                  tier={tier}
                  billingPeriod={billingPeriod}
                  onSelectTier={handleSelectTier}
                  currentUserTier={currentUserTier}
                  currentBillingPeriod={currentBillingPeriod}
                  isProcessing={isProcessing}
                />
              ))}
            </div>
          )}
          
          <PricingTestimonials />
          
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-8">Frequently Asked Questions</h2>
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-3">What's included in the Free plan?</h3>
                <p className="text-muted-foreground">
                  The Free plan includes 5 prompt copies per month, unlimited browsing of public prompts, 5 Product Copilot credits monthly, and community access. Perfect for trying out PromptHub.
                </p>
              </div>
              
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-3">What makes Product Copilot special?</h3>
                <p className="text-muted-foreground">
                  Product Copilot is your AI assistant for creating professional PRDs, project specs, and strategic documents. Free gets 5 credits/month, Plus gets 100 credits/month, Pro gets 300 credits/month for advanced capabilities.
                </p>
              </div>
              
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-3">How much do I save with yearly billing?</h3>
                <p className="text-muted-foreground">
                  You save 17% with yearly billing! Plus plan costs $100/year (instead of $120) and Pro plan costs $200/year (instead of $240).
                </p>
              </div>
              
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-3">What's the ROI of upgrading?</h3>
                <p className="text-muted-foreground">
                  Users typically save 5+ hours per week with our premium features. At $50/hour value, that's $1,300/month in time savings for a $10-20/month investment.
                </p>
              </div>
              
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-3">Can I upgrade or downgrade anytime?</h3>
                <p className="text-muted-foreground">
                  Yes, you can change your subscription plan at any time. Changes take effect immediately, and billing is prorated.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PricingPage;
